/**
 * Кто куда может зайти — единая таблица для маршрутов, меню и плиток.
 *
 * Раньше правило жило в трёх местах: обёртка маршрута в App.js, флажок у пункта
 * меню в Layout.js и ещё один на плитке страницы. Они расходились: пункт меню или
 * кнопка вели на закрытый экран, закрытый экран уводил на /processing, у которого
 * тоже нет права, — и человек видел белую страницу (аудит UI 25.09, этап 0).
 * Теперь маршрут, пункт меню и кнопка спрашивают одно и то же: canOpen(адрес).
 *
 * Каждое правило — функция от useAuth(): { user, isAdmin, isCompanyAdmin,
 * isInternalStaff, hasPermission, canFinance }.
 */
import { matchPath } from 'react-router-dom';
import { isCloudBrand } from '../contexts/BrandContext';

// ── Правила ────────────────────────────────────────────────────────────────
const anyUser = () => true;                                      // ProtectedRoute
const adminOnly = (a) => !!a.isAdmin;                            // AdminRoute
const companyAdmin = (a) => !!(a.isAdmin || a.isCompanyAdmin);   // CompanyAdminRoute
const finance = (a) => !!a.canFinance;                           // FinanceRoute
const perm = (section) => (a) => a.hasPermission(section);       // PermissionRoute
const internalPerm = (section) => (a) => !!a.isInternalStaff && a.hasPermission(section); // InternalSectionRoute
// Бывшие «только admin» экраны (разбор прав 05.10) — тоже internalPerm/staffOr с разделом
// «только явно» (AuthContext.EXPLICIT_SECTIONS): пускает администратор или отмеченная галочка.
// Внутренний сотрудник, у которого есть хотя бы один из разделов
const internalAny = (...sections) => (a) =>
  !!a.isInternalStaff && sections.some((s) => a.hasPermission(s));
// Складу — по разделу, клиенту — по своему правилу
const staffOr = (staffRule, clientRule) => (a) =>
  (a.user?.company_id ? clientRule(a) : staffRule(a));

// Заказы ФБС: складу — по разделу; клиенту — ещё и только при включённом ФБС в
// кабинете (auth/me → client_fbs). Без ФБС пункты вели в пустой экран с обещанием
// «заказы появятся после синхронизации» (аудит №3, F9). Пока /me не ответил
// (старый сохранённый профиль без поля) — не прячем.
const fbsPerm = (a) => a.hasPermission('processing')
  && (!a.user?.company_id || a.user?.client_fbs !== false);

// Разделы «Центра ФБС»: без хотя бы одного из них в центре нечего открыть
const FBS_HUB_SECTIONS = ['processing', 'scanning', 'fbs_zone', 'placement', 'pallet_audit', 'pallet_audit_confirm'];

/**
 * Маршруты приложения: шаблон адреса (как в <Route path>), название раздела
 * для сообщения «Нет доступа», правило. Порядок не важен — сверка по matchPath.
 */
export const ROUTE_ACCESS = [
  { path: '/companies', label: 'Компании', allow: staffOr(perm('companies'), companyAdmin) },
  { path: '/companies/:id', label: 'Карточка компании', allow: staffOr(perm('companies'), companyAdmin) },
  // Отгрузки и создание поставки — раздел 'orders', как пункт меню. Раньше адрес
  // был открыт любому вошедшему, и кладовщик без прав попадал в «Создание поставки».
  { path: '/orders', label: 'Заявки на отгрузку', allow: perm('orders') },
  { path: '/orders/create', label: 'Создание отгрузки', allow: perm('orders') },
  // Баркоды в Excel — инструмент каталога (решение владельца 27.09): складу — по
  // своему разделу, клиенту — по «Товарам», только своя компания (бэкенд проверяет)
  { path: '/barcodes', label: 'Баркоды в Excel', allow: staffOr(perm('barcodes'), perm('products')) },
  // Решение владельца 25.09 «склад сам выбирает»: отдельный раздел, раздаётся по должностям
  { path: '/calculator', label: 'Калькулятор WB', allow: internalPerm('calculator') },
  { path: '/calculation', label: 'Расчёт стоимости', allow: internalPerm('calculation') },
  { path: '/fbs-calculator', label: 'Калькулятор ФБС', allow: internalPerm('calculation') },
  { path: '/processing', label: 'Отгрузки', allow: perm('processing') },
  { path: '/processing/:id', label: 'Отгрузки', allow: perm('processing') },
  { path: '/processing/supply/:supplyId', label: 'Состав поставки', allow: perm('processing') },
  { path: '/fbs', label: 'Заказы ФБС', allow: fbsPerm },
  // Центр ФБС и Ozon ФБС были открыты любому внутреннему сотруднику, даже без
  // единого раздела: первый экран — счётчики руководителя и сплошные 403.
  { path: '/fbs-hub', label: 'Центр ФБС', allow: internalAny(...FBS_HUB_SECTIONS) },
  { path: '/ozon-fbs', label: 'Ozon ФБС', allow: internalPerm('processing') },
  { path: '/fbs-dashboard', label: 'Сроки и машины', allow: fbsPerm },
  { path: '/trucks', label: 'Рейсы', allow: internalPerm('trucks') },
  // Клиенту дашборд — его главная. Складу он показывает сводку обработки
  // (/api/processing/dashboard): без раздела 'processing' там только «Ошибка загрузки».
  { path: '/dashboard', label: 'Стадии отгрузок', allow: staffOr(perm('processing'), anyUser) },
  // Клиенту — нет: своих людей он заводит во вкладке «Пользователи» карточки компании.
  // Здесь разделы склада (403) и служебные учётки (аудит интерфейса 25.09).
  { path: '/users', label: 'Сотрудники', allow: staffOr(companyAdmin, () => false) },
  { path: '/staff-access', label: 'Персонал и доступы', allow: internalPerm('staff_access') },
  { path: '/shift', label: 'Смена', allow: internalPerm('shift') },
  { path: '/temp-workers', label: 'Подработчики', allow: internalPerm('temp_workers') },
  { path: '/warehouse', label: 'Остатки', allow: perm('warehouse') },
  { path: '/arrivals', label: 'Приёмка', allow: perm('arrivals') },
  { path: '/pickups', label: 'Заборы', allow: perm('arrivals') },
  { path: '/pvz-returns', label: 'Возвраты ПВЗ', allow: perm('arrivals') },
  { path: '/pvz-returns/:id', label: 'Возвраты ПВЗ', allow: perm('arrivals') },
  { path: '/marking', label: 'Маркировка', allow: perm('marking') },
  { path: '/picking', label: 'Подбор', allow: perm('picking') },
  { path: '/scanning', label: 'Сканирование', allow: perm('scanning') },
  { path: '/reprint', label: 'Повторная печать', allow: internalPerm('marking') },  // клиенту закрыто (этап 0 аудита интерфейса)
  { path: '/fbs-assembly', label: 'Сборка ФБС', allow: perm('scanning') },
  { path: '/where-is', label: 'Где лежит товар', allow: perm('scanning') },
  { path: '/monitor', label: 'Живая лента', allow: internalPerm('activity') },
  { path: '/services', label: 'Услуги', allow: internalPerm('services_tz') },
  { path: '/admin/ai', label: 'ИИ / OpenRouter', allow: adminOnly },
  // Облако (28.09): бренд/домен склада и реквизиты исполнителя — администратору склада
  { path: '/settings/brand', label: 'Бренд и домен', allow: adminOnly },
  { path: '/settings/requisites', label: 'Реквизиты', allow: adminOnly },
  // Облако Н3: мастер первого запуска склада (у Full-Fix сервер отвечает enabled=false)
  { path: '/setup', label: 'Мастер первого запуска', allow: adminOnly },
  // Облако: главная нового склада «С чего начать» (у Full-Fix страница уводит на обычную главную)
  { path: '/start', label: 'С чего начать', allow: adminOnly },
  // 28.09: «Каталог модулей» склада облака — включить/выключить модули тарифа
  { path: '/modules', label: 'Каталог модулей', allow: adminOnly },
  { path: '/subscription', label: 'Тариф и оплата', allow: adminOnly },
  { path: '/product-services-tz', label: 'ТЗ товаров', allow: staffOr(perm('services_tz'), companyAdmin) },
  { path: '/products', label: 'Товары', allow: perm('products') },
  { path: '/products/:id', label: 'Товары', allow: perm('products') },
  { path: '/marketplace', label: 'Маркетплейсы', allow: perm('marketplace') },
  { path: '/analytics', label: 'Аналитика', allow: internalPerm('analytics') },  // у клиента все её запросы получают 403 — экран только складу
  { path: '/warehouse-activity', label: 'Работа склада', allow: internalPerm('activity') },
  { path: '/stock-log', label: 'Движение остатков', allow: internalPerm('stock_log') },
  { path: '/stock-transfer', label: 'Перенос остатка', allow: staffOr(internalPerm('stock_writeoff'), perm('products')) },
  // Загрузка/выгрузка каталога Excel: складу — свой раздел (аудит UI №62), клиенту — по «Товарам», как было
  { path: '/product-management', label: 'Загрузка Excel', allow: staffOr(perm('product_import'), perm('products')) },
  { path: '/arrivals/:id', label: 'Приёмка', allow: perm('arrivals') },
  { path: '/arrivals/:id/warehouse', label: 'Приёмка', allow: perm('arrivals') },
  { path: '/arrivals/:id/boxes', label: 'Приёмка по коробам', allow: perm('arrivals') },
  { path: '/finances', label: 'Финансы', allow: finance },
  { path: '/money', label: 'Деньги', allow: finance },
  { path: '/payroll', label: 'Финансы', allow: finance },
  { path: '/crm', label: 'CRM', allow: internalPerm('crm') },
  { path: '/crm/home', label: 'CRM', allow: internalPerm('crm') },
  { path: '/crm/overview', label: 'CRM', allow: internalPerm('crm_all') },
  { path: '/crm/deals/:id', label: 'CRM', allow: internalPerm('crm') },
  { path: '/crm/leads', label: 'CRM', allow: internalPerm('crm') },
  { path: '/crm/leads/:id', label: 'CRM', allow: internalPerm('crm') },
  { path: '/crm/tasks', label: 'CRM', allow: internalPerm('crm') },
  { path: '/crm/contacts', label: 'CRM', allow: internalPerm('crm') },
  { path: '/crm/contacts/:id', label: 'CRM', allow: internalPerm('crm') },
  { path: '/crm/organizations', label: 'CRM', allow: internalPerm('crm') },
  { path: '/crm/organizations/:id', label: 'CRM', allow: internalPerm('crm') },
  { path: '/crm/reports', label: 'CRM', allow: internalPerm('crm') },
  { path: '/crm/audit', label: 'CRM', allow: internalPerm('crm') },
  { path: '/crm/trash', label: 'CRM', allow: internalPerm('crm_delete') },
  { path: '/crm/settings', label: 'CRM', allow: internalPerm('crm_settings') },
  { path: '/marketing', label: 'Маркетинг', allow: internalPerm('marketing') },
  { path: '/storage', label: 'Ячейки', allow: perm('storage') },
  // Конструктор склада (28.09): зоны и стеллажи планом — складу, клиенту закрыто, как и сами ячейки
  { path: '/warehouse-builder', label: 'Конструктор склада', allow: internalPerm('storage') },
  { path: '/placement', label: 'Размещение товара (штучно)', allow: perm('placement') },
  { path: '/box-placement', label: 'Размещение коробов', allow: perm('placement') },
  { path: '/fbs-move', label: 'Перемещение', allow: perm('placement') },
  { path: '/fbs-zone-control', label: 'Контроль зоны', allow: perm('placement') },
  { path: '/fbs-return-buffer', label: 'Вернуть на Основной', allow: perm('placement') },
  { path: '/otg-return', label: 'Вернуть из ячеек отгрузки', allow: perm('placement') },
  { path: '/fbs-unaccepted', label: 'Не принято WB', allow: internalPerm('processing') },  // клиенту закрыто (этап 0 аудита интерфейса)
  { path: '/fbs-check/:batch', label: 'Проверочная партия', allow: perm('processing') },
  { path: '/box-recount', label: 'Пересчитать короб', allow: perm('placement') },
  { path: '/kiz-trace', label: 'Где коды', allow: internalPerm('kiz') },
  { path: '/zone-ledger', label: 'Реестр зоны', allow: internalPerm('zone_ledger') },
  // Экран руководителя (списания по сверкам) — свой раздел, отдельно от переклички (аудит UI №63)
  { path: '/pallet-audit-confirm', label: 'Расхождения', allow: internalPerm('pallet_audit_confirm') },
  { path: '/pallet-audit', label: 'Сверка палеты', allow: internalPerm('pallet_audit') },
  // Объединённый экран: ячейки закрытых документов (возврат — «Размещение»,
  // проверка — «Сверка палеты») и сверка палеты. Вкладки — по правам внутри.
  { path: '/cell-audit', label: 'Ячейки и палеты', allow: internalAny('placement', 'pallet_audit') },
  { path: '/fbs-zone-intake', label: 'Пополнение зоны', allow: internalPerm('fbs_zone') },
  { path: '/fbs-recount', label: 'Переучёт зоны', allow: internalPerm('fbs_zone') },
  { path: '/fbs-tails', label: 'Хвосты', allow: internalPerm('fbs_zone') },
  { path: '/board', label: 'Склад сейчас', allow: internalPerm('board') },
  { path: '/work', label: 'Моя смена', allow: internalPerm('work') },
  // Задачи (27.10): складу — раздел tasks, клиенту — любой пользователь компании (свои задачи складу)
  { path: '/tasks', label: 'Задачи', allow: staffOr(perm('tasks'), anyUser) },
  { path: '/tasks/:id', label: 'Задача', allow: staffOr(perm('tasks'), anyUser) },
  { path: '/tsd', label: 'Терминалы (ТСД)', allow: internalPerm('equipment') },
  { path: '/printers', label: 'Принтеры', allow: internalPerm('equipment') },
  { path: '/shipping-warehouses', label: 'Склады отгрузки', allow: perm('shipping') },
  // Складу — раздел «Шаблоны этикеток», клиенту — как было (адрес открыт, пункт меню по «Маркировке»)
  { path: '/label-templates', label: 'Шаблоны этикеток', allow: staffOr(perm('label_templates'), anyUser) },
  // Брак/пересорт — как пункт меню: складу раздел 'warehouse', клиенту 'processing'
  // Складу — по своему разделу «Брак и пересорт» (раньше галочка ни на что не влияла:
  // пускали «Остатки»/«Отгрузки»; на 26.09 у всех, у кого они были, defects тоже есть).
  { path: '/defects', label: 'Брак / Пересорт', allow: staffOr(perm('defects'), perm('processing')) },
  { path: '/client-brak', label: 'Брак в ячейках', allow: perm('storage') },
  { path: '/storage-billing', label: 'Хранение', allow: staffOr(perm('billing'), companyAdmin) },
  // «Финансы» со списком клиентов и балансами. Клиенту — админу компании (как
  // пункт меню), складу — администратору или сотруднику с доступом к финансам.
  // Раньше адрес открывался любому вошедшему, в том числе кладовщику без прав.
  { path: '/finance', label: 'Финансы', allow: staffOr((a) => !!(a.isAdmin || a.canFinance || a.hasPermission('billing')), anyUser) },  // клиенту — как было (любой пользователь компании)
  { path: '/consumables', label: 'Расходники', allow: internalPerm('consumables') },
  { path: '/my-consumables', label: 'Расходники', allow: companyAdmin },
  { path: '/admin/finance', label: 'Финансы (админ)', allow: internalPerm('billing') },
  { path: '/integrations/api', label: 'API', allow: companyAdmin },
  { path: '/onboarding', label: 'Настройка кабинета', allow: companyAdmin },
  { path: '/knowledge/:category/:slug', label: 'База знаний', allow: anyUser },
  { path: '/knowledge/:category', label: 'База знаний', allow: anyUser },
  { path: '/knowledge', label: 'База знаний', allow: anyUser },
  { path: '/whats-new', label: 'Что нового', allow: anyUser },
  { path: '/admin/knowledge-base', label: 'База знаний (админ)', allow: internalPerm('kb_editor') },
  { path: '/profile', label: 'Профиль', allow: anyUser },
  { path: '/admin/notifications', label: 'Уведомления и бот', allow: internalPerm('notifications') },
  { path: '/ui-kit', label: 'UI-кит', allow: adminOnly },
  { path: '/ui-demo', label: 'UI-кит', allow: adminOnly },
  // Облако Н3: кабинет платформы — только владелец Full-Fix (сервер: require_platform_owner)
  { path: '/platform-admin', label: 'Кабинет платформы', allow: (a) => !!a.isPlatformOwner },
];

/**
 * Облако Н3: модуль тарифа, без которого маршрут закрыт (и пункт меню не виден).
 * Модули приходят с сервера (/api/platform/plan → useAuth().modules); у Full-Fix
 * modules = null — открыто всё, как раньше. Внутренние разделы Full-Fix (финансы
 * владельца, зарплата, BossControl, служебные сверки, «Рейсы») у склада облака
 * выключены всегда. Сервер закрывает те же разделы сам (app/plan_gates.py).
 *
 * 28.09 «Каталог модулей»: модули = тариф ∩ выбор администратора склада. Каталог
 * (названия, описания, зависимости) — backend/app/platform/modules_catalog.py.
 * Значение — ключ модуля или список ключей: тогда хватит любого (зона ФБС нужна и
 * WB, и Ozon).
 */
export const ROUTE_MODULES = {
  '/finances': 'finance_owner',
  '/money': 'finance_owner',
  '/payroll': 'finance_owner',
  '/staff-access': 'bosscontrol',
  '/shift': 'bosscontrol',
  '/temp-workers': 'bosscontrol',
  '/zone-ledger': 'internal_audit',
  '/fbs-tails': 'internal_audit',
  '/pallet-audit-confirm': 'internal_audit',
  // Отзыв владельца 28.09: в облаке «Все разделы» — только то, что нужно клиентам;
  // «Баркоды в Excel» и «Брак · В ячейках брака» — наши инструменты, у full-fix как было
  '/barcodes': 'internal_audit',
  '/client-brak': 'internal_audit',
  '/pallet-audit': 'pallet_audit',
  '/trucks': 'trucks',
  '/admin/ai': 'ai_admin',
  '/admin/knowledge-base': 'kb_editor',
  '/crm': 'crm',
  '/crm/home': 'crm',
  '/crm/overview': 'crm',
  '/crm/deals/:id': 'crm',
  '/crm/leads': 'crm',
  '/crm/leads/:id': 'crm',
  '/crm/tasks': 'crm',
  '/crm/contacts': 'crm',
  '/crm/contacts/:id': 'crm',
  '/crm/organizations': 'crm',
  '/crm/organizations/:id': 'crm',
  '/crm/reports': 'crm',
  '/crm/audit': 'crm',
  '/crm/trash': 'crm',
  '/crm/settings': 'crm',
  '/marketing': 'crm',
  '/calculator': 'calculator',
  '/calculation': 'calculator',
  '/fbs-calculator': 'calculator',
  '/ozon-fbs': 'ozon_fbs',
  '/kiz-trace': 'kiz_trace',
  '/warehouse-activity': 'warehouse_activity',
  // Биллинг по ТЗ (до 28.09 — часть «Клиентов»)
  '/admin/finance': 'billing',
  '/finance': 'billing',
  '/services': 'billing',
  '/product-services-tz': 'billing',
  '/storage-billing': 'billing',
  '/consumables': 'consumables',
  '/my-consumables': 'consumables',
  // Wildberries ФБС
  '/fbs': 'wb_fbs',
  '/fbs-dashboard': 'wb_fbs',
  '/fbs-unaccepted': 'wb_fbs',
  '/fbs-check/:batch': 'wb_fbs',
  '/fbs-assembly': 'wb_fbs',
  // Зона ФБС — любой ФБС
  '/fbs-hub': ['wb_fbs', 'ozon_fbs'],
  '/fbs-zone-intake': ['wb_fbs', 'ozon_fbs'],
  '/fbs-move': ['wb_fbs', 'ozon_fbs'],
  '/fbs-zone-control': ['wb_fbs', 'ozon_fbs'],
  '/fbs-return-buffer': ['wb_fbs', 'ozon_fbs'],
  '/fbs-recount': ['wb_fbs', 'ozon_fbs'],
  '/box-recount': ['wb_fbs', 'ozon_fbs'],
  '/pvz-returns': 'pvz_returns',
  '/pvz-returns/:id': 'pvz_returns',
  '/marking': 'marking',
  '/integrations/api': 'api',
  '/admin/notifications': 'bot',
  '/tsd': 'tsd',
};

/** Включён ли модуль маршрута: ключ или список «любой из». */
export function routeModuleOn(mod, auth) {
  if (!mod || !auth.moduleOn) return true;
  return Array.isArray(mod) ? mod.some((m) => auth.moduleOn(m)) : auth.moduleOn(mod);
}

/** Правило маршрута + модуль тарифа: одно решение для Guard, меню и кнопок. */
export function allowRule(rule, auth) {
  if (!rule || !rule.allow(auth)) return false;
  return routeModuleOn(ROUTE_MODULES[rule.path], auth);
}

const BY_PATTERN = new Map(ROUTE_ACCESS.map((r) => [r.path, r]));

/** Правило по шаблону маршрута из App.js (точное совпадение строки). */
export function ruleFor(pattern) {
  return BY_PATTERN.get(pattern) || null;
}

/** Правило по живому адресу: «/processing/123?x=1» → правило «/processing/:id». */
export function ruleForUrl(url) {
  if (!url) return null;
  const pathname = String(url).split(/[?#]/)[0].replace(/\/+$/, '') || '/';
  // Статичные шаблоны раньше параметрических: «/orders/create» не должен
  // совпасть с чем-то вроде «/orders/:id», если такой появится.
  let best = null;
  for (const r of ROUTE_ACCESS) {
    if (matchPath({ path: r.path, end: true }, pathname)) {
      if (!best || (best.path.includes(':') && !r.path.includes(':'))) best = r;
    }
  }
  return best;
}

/**
 * Можно ли этому пользователю открыть адрес. Адрес не из таблицы (внешний,
 * устаревший) считаем закрытым: кнопка в пустоту хуже, чем отсутствие кнопки.
 */
export function canOpen(url, auth) {
  if (!auth?.user) return false;
  const r = ruleForUrl(url);
  return allowRule(r, auth);
}

/**
 * «Домашний» экран роли — куда вести после входа и с закрытого адреса.
 * Всегда возвращает адрес, который этот человек откроет: последний запасной
 * вариант — профиль, он открыт любому вошедшему. Поэтому редирект на себя
 * (и белый экран) невозможен.
 */
const HOME_CANDIDATES_STAFF = [
  '/work', '/fbs-assembly', '/arrivals', '/box-placement', '/board', '/processing',
  '/fbs-hub', '/fbs-zone-intake', '/where-is', '/warehouse', '/marking', '/products',
  '/crm', '/calculation', '/finances', '/shift',
];
const HOME_CANDIDATES_CLIENT = ['/dashboard', '/arrivals', '/processing', '/products', '/marking'];

export function homePath(auth, opts) {
  if (!auth?.user) return '/login';
  if (auth.user.company_id) {
    return HOME_CANDIDATES_CLIENT.find((p) => canOpen(p, auth)) || '/profile';
  }
  // Облако: администратор попадал на пустые «Все отгрузки». Мастер и «С чего начать»
  // выбирает HomeRedirect («/»); сюда же ведут и после входа — «Склад сейчас».
  const cloud = !!(opts?.cloud || auth.plan?.cloud || isCloudBrand());
  if (cloud && auth.isAdmin && canOpen('/board', auth)) return '/board';
  // Администратору — обработка заказов, как раньше
  if (auth.isAdmin && canOpen('/processing', auth)) return '/processing';
  // Склад облака: «Сборка ФБС» открывается по праву «Сканирование», и приёмщик
  // нового склада попадал в пустую сборку. Сборка первой — только у сборщика
  // (право «Подбор»), остальным — приёмка. Full-Fix — как раньше.
  let cands = HOME_CANDIDATES_STAFF;
  // cloud: тариф грузится после входа, поэтому признак облака передаёт и бренд (HomeRedirect)
  if (cloud && !auth.hasPermission?.('picking')) {
    cands = cands.filter((p) => p !== '/fbs-assembly');
    cands = [...cands.slice(0, cands.indexOf('/arrivals') + 1), '/fbs-assembly', ...cands.slice(cands.indexOf('/arrivals') + 1)];
  }
  return cands.find((p) => canOpen(p, auth)) || '/profile';
}
